import styled from "@emotion/styled"
import { Box, Button, Pagination, PaginationItem } from "@mui/material"
import { useTranslations } from "next-intl"
import { Fragment, useEffect, useMemo, useState } from "react"
import { Container, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Apartment } from "utils"
import ApartmentDrawerItem from "../Projects/Marketing/ApartmentMarketingForRent/ApartmentDrawerItem"
import DrawerContactForm from "../Projects/Marketing/ApartmentMarketingForRent/DrawerContactForm"
import { createDefaultState, filterApartments } from "../Projects/Marketing/PropertyTab"
import { FormInput as ApState } from "../Projects/Marketing/UmbracoApartments/FiltersModal"
import { ProjectsMessages } from "../Projects/Projects"
import MarketingBanner from "./MarketingBanner"

type Props = {
  allApartments: Apartment[]
  pageData: any
  messages: ProjectsMessages
  selectedFilters: string[]
  selectedRooms: string[]
  selectedProjects: string[]
  onResetAllFilters: () => void
  locale: string
  allApChar: string[]
  allBuChar: string[]
  companyId?: number
  cmsProjectLeadIds?: Record<string, number>
  noAvailableApartmentsBanner?: {
    title?: string
    text?: string
    button?: { name?: string; url?: string }
  }
}

const StyledApartmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 32px;
  padding-bottom: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
    padding-top: 32px;
    padding-bottom: 40px;
  }
`

const StyledPaginationDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 40px;
  }
  li {
    padding-left: 4px;
    ${(props) => props.theme.breakpoints.up("md")} {
      padding-left: 16px;
    }
    &:last-child {
      a {
        border: none !important;
      }
    }
    &:first-child {
      a {
        border: none !important;
      }
    }
    a {
      font-size: 16px;
      border: 1px solid #f7f7f7;
    }
    .Mui-selected {
      background-color: ${(props) => props.theme.colors.propBlue};
    }
  }
`

const StyledTop = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledDot = styled.div`
  width: 8px;
  height: 8px;
  background-color: ${sharedColors.green};
  border-radius: 50px;
`

const ResetFiltersLink = styled(Button)`
  text-decoration: underline;
  font-size: 14px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
  font-weight: 400;
  height: unset;
  line-height: normal;
  color: black;
  padding: 0;
  min-width: 0;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
    background-color: transparent;
  }
`

const StyledTotalDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px 16px;
  margin-top: 8px;
  margin-inline: -16px;
  border-radius: 0;
  background-color: #2768a3;
  overflow: hidden;
  position: relative;
  align-items: flex-start;
  text-align: start;
  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 168px;
    height: 168px;
    border-radius: 50%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.24), transparent);
  }
  &::before {
    left: -59px;
    top: -18px;
  }
  &::after {
    right: -83px;
    bottom: -28px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 8px;
    margin-inline: 0;
    gap: 32px;
    padding: 32px;
    border-radius: 16px;
    align-items: center;
    text-align: center;
    &::before,
    &::after {
      width: 200px;
      height: 200px;
    }
    &::before {
      left: -75px;
      top: -16px;
    }
    &::after {
      right: -99px;
      bottom: -40px;
    }
  }
`

const EmptyStateCta = styled(Button)`
  border-radius: 200px;
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  height: 48px;
  color: white;
  border: 2px solid white;
  align-self: flex-end;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-self: center;
  }
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: white;
  }
`

function filterAllApartments({
  apartments,
  selectedCities,
  selectedRooms,
  selectedProjects,
  state,
}: {
  apartments: Apartment[]
  selectedCities: string[]
  selectedRooms: string[]
  selectedProjects: string[]
  state: ApState
}) {
  let filteredApartments: Apartment[] = apartments
  if (!selectedCities.includes("all")) {
    filteredApartments = filteredApartments.filter((el) =>
      selectedCities.some((city) => el?.city?.includes(city)),
    )
  }
  if (!selectedRooms.includes("all")) {
    filteredApartments = filteredApartments.filter((el) =>
      selectedRooms.includes(el.numberOfRooms || ""),
    )
  }
  if (!selectedProjects.includes("all")) {
    filteredApartments = filteredApartments.filter((el) =>
      selectedProjects.includes(el.umbracoName || ""),
    )
  }
  const finalAp = filterApartments({ apartments: filteredApartments, state })
  return finalAp
}

function Apartments({
  pageData,
  messages,
  allApartments,
  selectedFilters,
  selectedRooms,
  selectedProjects,
  onResetAllFilters,
  locale,
  allApChar,
  allBuChar,
  companyId,
  cmsProjectLeadIds,
  noAvailableApartmentsBanner,
}: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Build project list for rental housing form
  const rentalProjects = useMemo(() => {
    const projectMap = new Map<string, { id: number; rooms: Set<string> }>()
    allApartments.forEach((apt) => {
      const name = apt.umbracoName
      if (!name) return
      const existing = projectMap.get(name) || {
        id: apt.leadFormBMBYId || Number(apt.priorityID) || 0,
        rooms: new Set<string>(),
      }
      if (apt.numberOfRooms) existing.rooms.add(apt.numberOfRooms)
      projectMap.set(name, existing)
    })
    return Array.from(projectMap.entries()).map(([name, data]) => ({
      name,
      id: data.id,
      rooms: Array.from(data.rooms).sort((a, b) => Number(a) - Number(b)),
    }))
  }, [allApartments])

  const [pageNum, setPageNum] = useState(1)
  const [filtersCount, setFiltersCount] = useState(0)

  const defaultState = useMemo(
    () => createDefaultState({ apartmProp: allApChar, buildProp: allBuChar }),
    [allApChar, allBuChar],
  )

  const [state, setState] = useState(defaultState)

  // Reset pagination when filters change.
  // Compared by value, not reference: the parent rebuilds these arrays on every
  // render, so a reference dep list resets the page on any unrelated re-render
  // such as opening the project dropdown.
  const filtersKey = [
    selectedFilters.join(","),
    selectedRooms.join(","),
    selectedProjects.join(","),
  ].join("|")

  useEffect(() => {
    setPageNum(1)
  }, [filtersKey])

  const showDefault =
    selectedFilters.includes("all") &&
    selectedRooms.includes("all") &&
    selectedProjects.includes("all") &&
    filtersCount === 0
  const perPage = 9
  const t = useTranslations("BuildingInfo")
  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNum(value)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }
  const filteredAllApartments = filterAllApartments({
    apartments: allApartments,
    selectedCities: selectedFilters,
    selectedRooms,
    selectedProjects,
    state,
  })
  const totalNumber = showDefault ? allApartments.length : filteredAllApartments.length

  // Calculate pagination for filtered results
  const startIndex = (pageNum - 1) * perPage
  const endIndex = startIndex + perPage
  const paginatedFilteredApartments = filteredAllApartments.slice(startIndex, endIndex)

  const displayedApartments = showDefault
    ? allApartments.slice(startIndex, endIndex)
    : paginatedFilteredApartments
  const handleAllReset = () => {
    setState(defaultState)
    setFiltersCount(0)
    onResetAllFilters()
    setPageNum(1)
  }

  const hasActiveFilters =
    !selectedFilters.includes("all") ||
    !selectedRooms.includes("all") ||
    !selectedProjects.includes("all") ||
    filtersCount > 0
  return (
    <>
      <Container>
        <StyledApartmentsContainer>
          <StyledTop>
            <>
              {totalNumber > 0 ? (
                <StyledTotalDiv>
                  <StyledDot />
                  <NewText
                    component={"h2"}
                    desktopFontSize={18}
                    desktopLineHeight={"normal"}
                    mobileFontSize={14}
                    mobileLineHeight={"normal"}
                    color={sharedColors.black}
                  >
                    {t("totalRent")} <strong>{totalNumber}</strong> {t("availableApartmentsRent")}
                  </NewText>
                </StyledTotalDiv>
              ) : hasActiveFilters ? (
                <NewText
                  component={"p"}
                  desktopFontSize={18}
                  desktopLineHeight={"normal"}
                  mobileFontSize={14}
                  mobileLineHeight={"normal"}
                  color={sharedColors.black}
                >
                  {t("noApartmentsFound")}
                </NewText>
              ) : null}
              {hasActiveFilters && (
                <ResetFiltersLink onClick={handleAllReset}>{t("resetFilters")}</ResetFiltersLink>
              )}
            </>
          </StyledTop>
          {displayedApartments.map((item, index: number) => {
            return (
              <Fragment key={index}>
                {pageNum === 1 && index === 5 && displayedApartments.length !== 6 && (
                  <MarketingBanner data={pageData} />
                )}

                {pageNum !== 1 && index % 4 === 0 && index !== 0 && (
                  <MarketingBanner data={pageData} />
                )}
                <ApartmentDrawerItem
                  apartment={item}
                  formSubjects={{}}
                  messages={messages}
                  bmbyId={item.leadFormBMBYId || Number(item.priorityID)}
                  companyId={companyId}
                  locale={locale}
                  status={"MarketingAndRental"}
                  phone={item.projectPhone}
                  rentalProjects={rentalProjects}
                  cmsProjectLeadIds={cmsProjectLeadIds}
                  preSelectedProject={item.umbracoName}
                />
                {pageNum === 1 && index === 5 && displayedApartments.length === 6 && (
                  <MarketingBanner data={pageData} />
                )}
              </Fragment>
            )
          })}
          {pageNum === 1 && displayedApartments.length < 5 && displayedApartments.length > 0 && (
            <MarketingBanner data={pageData} />
          )}
          {displayedApartments.length < 1 && (
            <StyledEmptyState>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <NewText
                  component={"h3"}
                  desktopFontSize={32}
                  desktopLineHeight={"36px"}
                  mobileFontSize={24}
                  mobileLineHeight={"normal"}
                  color={"white"}
                  sx={{ maxWidth: 675 }}
                >
                  {noAvailableApartmentsBanner?.title || t("noApartmentsTitle")}
                </NewText>
                <NewText
                  component={"p"}
                  desktopFontSize={18}
                  desktopLineHeight={"normal"}
                  mobileFontSize={18}
                  mobileLineHeight={"normal"}
                  color={"white"}
                  sx={{ maxWidth: 535 }}
                >
                  {noAvailableApartmentsBanner?.text || t("noApartmentsText")}
                </NewText>
              </Box>
              <EmptyStateCta variant="outlined" onClick={() => setDrawerOpen(true)}>
                {noAvailableApartmentsBanner?.button?.name || t("joinWaitingList")}
              </EmptyStateCta>
            </StyledEmptyState>
          )}
        </StyledApartmentsContainer>
        {totalNumber > 0 && (
          <StyledPaginationDiv>
            <Pagination
              count={Math.ceil(
                (showDefault ? allApartments : filteredAllApartments).length / perPage,
              )}
              color="primary"
              onChange={handleChange}
              defaultPage={1}
              page={pageNum}
              size="small"
              renderItem={(item) => <PaginationItem {...item} />}
            />
          </StyledPaginationDiv>
        )}
      </Container>
      <DrawerContactForm
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        rentalHousingFormProps={{
          onClose: () => setDrawerOpen(false),
          messages,
          companyId,
          projects: rentalProjects,
          cmsProjectLeadIds,
          noAvailableApartements: true,
        }}
      />
    </>
  )
}

export default Apartments
